import { useState, useMemo, useEffect, type ChangeEvent } from "react";
import {
  Label,
  Flex,
  TextOrHTML,
  Switch,
  Accordion,
} from "@simplybusiness/mobius";
import classNames from "classnames/dedupe";
import { useAtom } from "jotai";
import type { QuestionComponentProps } from "../../../types";
import {
  getOffValue,
  getIsChecked,
  getInitialSelectedValue,
  isMandatoryCover,
} from "../utils";
import { isNoCoverValue } from "../constants";
import {
  isShadowQuestion,
  useQuestionnaire,
} from "../../../../../../../../features/questionnaire";
import { CoverLimitEditor } from "../CoverLimitEditor";
import { HelpLink } from "../../../../HelpLink";
import { useSnowplowContext } from "@simplybusiness/services";
import { personalisedCoverAccordionAtom } from "../../../../../../../../store";
import { ChatbotPrompts } from "../ChatbotPrompts";
import "../PersonalisedCoverQuestion.css";

const MIN_OPTIONS_FOR_COVER_EDITOR = 3;

export const PersonalisedCoverQuestion = ({
  questionFieldProps,
  options,
  chatbotPrompts,
}: QuestionComponentProps) => {
  const {
    id,
    name,
    value,
    onChange,
    questionLabelProps: {
      primaryText,
      secondaryText,
      labelClasses,
      labelElementType,
      labelProps,
    },
    isRequired,
    errorMessage,
    helpInfo,
    isInvalid,
    ...otherProps
  } = questionFieldProps;

  const offValue = useMemo(() => getOffValue(options), [options]);
  const isMandatory = useMemo(() => isMandatoryCover(options), [options]);
  const isChecked = getIsChecked(value, offValue, options);

  const [openAccordion, setOpenAccordion] = useAtom(
    personalisedCoverAccordionAtom,
  );
  const isAccordionOpen = openAccordion === name;

  const [selectedValue, setSelectedValue] = useState(() =>
    getInitialSelectedValue(value, options, offValue),
  );

  const { groups, questions, removeError } = useQuestionnaire();
  const { snowplow } = useSnowplowContext();

  useEffect(() => {
    const hasInvalidValue = typeof value === "string" && isNoCoverValue(value);

    if (isMandatory && (!value || hasInvalidValue) && selectedValue) {
      onChange?.({
        target: { name: name, value: selectedValue },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    toggleGroupQuestions(isAccordionOpen && (isMandatory || isChecked));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAccordionOpen]);

  const toggleGroupQuestions = (show: boolean) => {
    const groupQuestions = groups?.[name]?.names || [];

    const questionsToToggle = groupQuestions.filter(questionName => {
      const question = questions.filter.getByName(questionName);
      return question?.visible ?? false;
    });

    if (questionsToToggle.length > 0) {
      if (show) {
        questions.show(questionsToToggle);
      } else {
        questions.hide(questionsToToggle);
      }
    }
  };

  const handleSwitchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    const newValue = checked ? selectedValue : offValue;

    const isUncheckedShadowQuestion = !checked && isShadowQuestion(name);

    onChange?.({
      target: {
        name: name,
        value: newValue,
        shouldUpdate: isUncheckedShadowQuestion,
      },
    });

    if (!checked) {
      toggleGroupQuestions(false);
      const groupQuestions = groups?.[name]?.names;
      for (const questionName of groupQuestions || []) {
        removeError(questionName);
      }
    }

    if (checked) {
      setOpenAccordion(name);
    } else if (isAccordionOpen) {
      setOpenAccordion(undefined);
    }
  };

  const handleAccordionChange = (open: boolean) => {
    setOpenAccordion(open ? name : undefined);
  };

  const handleAccordionOpen = () => {
    snowplow?.trigger("coverDetailsClicked", {
      property: primaryText,
    });
  };

  const handleCoverLimitChange = (newValue: string) => {
    setSelectedValue(newValue);

    if (isMandatory || isChecked) {
      onChange?.({
        target: { name: name, value: newValue },
      });
    }
  };

  return (
    <div className="mobius-personalised-cover-question">
      <Flex justifyContent="space-between" alignItems="center">
        <Label
          className={classNames(
            labelClasses,
            "mobius-personalised-cover-question__label",
          )}
          elementType={labelElementType}
          {...labelProps}
        >
          <TextOrHTML text={primaryText} />
        </Label>
        {!isMandatory && (
          <Switch
            {...otherProps}
            checked={isChecked}
            onChange={handleSwitchChange}
          />
        )}
      </Flex>
      {options &&
        (isMandatory || options.length >= MIN_OPTIONS_FOR_COVER_EDITOR) && (
          <CoverLimitEditor
            selectedValue={selectedValue}
            options={options}
            onChange={handleCoverLimitChange}
          />
        )}
      <Accordion
        showText="More info"
        hideText="Less info"
        startOpen={isAccordionOpen}
        onChange={handleAccordionChange}
        onOpen={handleAccordionOpen}
      >
        {secondaryText && (
          <TextOrHTML text={secondaryText as string} textWrapper />
        )}
        {helpInfo && <HelpLink {...helpInfo} label="Cover details" />}
      </Accordion>
      {chatbotPrompts && chatbotPrompts.length > 0 && (
        <ChatbotPrompts chatbotPrompts={chatbotPrompts} productCode={id} />
      )}
    </div>
  );
};
